/**
 * MainReviewCard - Large hero card showing yearly Wovar rating
 * Displays brand image, rating, and total review count
 *
 * Image sizing:
 * - Desktop (lg+): 376px x 156px
 * - Mobile/Tablet: 100% width, auto height (maintains aspect ratio)
 */

import type { FC } from "react";
import { useLocale } from "@commercekit/remix";
import { useLocalePath } from "@commercekit/remix/lib/hooks/useLocale";
import { StarRating } from "./StarRating";
import { formatRating } from "./utils";
import { PrismicImage } from "@prismicio/react";
import type { ImageField } from "@commercekit/prismic";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import ChevronDownIcon from "../icons/ChevronDownIcon";
import WvLink from "~/components/atoms/WvLink";

interface MainReviewCardProps {
  rating: number;
  totalReviews: number;
  brandImage?: ImageField;
  href?: string;
}

export const MainReviewCard: FC<MainReviewCardProps> = ({
  rating,
  totalReviews,
  brandImage,
  href,
}) => {
  const { localeFullName } = useLocale();
  const { $pt } = usePrismicLabels();
  const localePath = useLocalePath();
  const isFooterVariant = Boolean(href);

  const scrollToReviews = (e: React.MouseEvent) => {
    e.preventDefault();
    document
      .getElementById("customer-reviews-title")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="text-center d-flex flex-column align-items-center justify-content-center bg-white reviews_main_card_section"
    >
      {brandImage && (
        <div className="w-100 mb-4">
          <PrismicImage
            field={brandImage}
            width={376}
            height={156}
            className="d-block mr-auto img-fluid reviews_main_card_image"
          />
        </div>
      )}

      <div className="d-flex flex-row align-items-center w-100 mb-3 reviews_main_card_rating">
        <div className="d-flex flex-column align-items-start justify-content-center">
          <StarRating rating={rating} size="xl" />
          <p className="mb-0 reviews_main_card_rating_text_paragraph">{totalReviews.toLocaleString()}+ reviews</p>
        </div>
        <span
          className={`font-weight-bold text-blue-light reviews_main_card_rating_text${
            isFooterVariant ? "" : " mr-5"
          }`}
        >
          {formatRating(rating, localeFullName)}
        </span>
      </div>

      {href ? (
        <WvLink
          to={localePath(href)}
          className="d-flex flex-row align-items-center w-100 mt-auto text-decoration-none read_more-reviews_link"
        >
          <span className="reviews_main_card_rating_read-reviews">
            {$pt("footer_read_reviews", {
              rating: formatRating(rating, localeFullName),
            })}
          </span>
          <span className="ml-2 footer_reviews_main_card_rating_read-reviews_icon">
              <ChevronDownIcon />
          </span>
        </WvLink>
      ) : (
        <div
          onClick={scrollToReviews}
          className="d-flex flex-row align-items-center w-100 mt-auto text-decoration-none read_more-reviews_link"
        >
          <span className="reviews_main_card_rating_read-reviews">
            {$pt("scroll_to_read_reviews", {rating: formatRating(rating, localeFullName)})}
          </span>
          <span className="ml-2 reviews_main_card_rating_read-reviews_icon">
            <ChevronDownIcon />
          </span>
        </div>
      )}
    </div>
  );
};

export default MainReviewCard;
